import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';

import { ClassicPreset, NodeEditor } from 'rete';
import { AreaPlugin } from 'rete-area-plugin';
import { ConnectionPlugin, Presets as ConnectionPresets } from 'rete-connection-plugin';
import { AngularPlugin, ControlComponent, Presets } from 'rete-angular-plugin/21';

import type { EntityFieldRow, ViewFieldNodeState } from '../mapping-types';
import { ViewFieldControlComponent, type ViewFieldControlPayload } from './nodes/viewfield-control.component';
import { LiteralControlComponent, type LiteralControlPayload, type LiteralKind } from './nodes/literal-control.component';
import { ComparatorControlComponent, type ComparatorControlPayload, type ComparatorOp } from './nodes/comparator-control.component';
import { FormulaControlComponent, type FormulaControlPayload, type FormulaFn } from './nodes/formula-control.component';
import { buildFormulaEmitter, type GraphConnection, type GraphNodeInfo } from './formula-graph';

export interface ReteConnection {
  fromEntityFieldName: string;
  toViewNodeId: string;
}

@Component({
  selector: 'app-rete-canvas',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './rete-canvas.component.html',
  styleUrl: './rete-canvas.component.scss',
})
export class ReteCanvasComponent implements AfterViewInit, OnChanges {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  @Input({ required: true }) entityFields: EntityFieldRow[] = [];
  @Input({ required: true }) viewNodes: ViewFieldNodeState[] = [];
  @Input({ required: true }) connections: ReteConnection[] = [];
  @Input() selectedViewNodeId: string | null = null;

  @Output() connectionsChange = new EventEmitter<ReteConnection[]>();
  @Output() selectViewNode = new EventEmitter<string | null>();
  @Output() patchViewNode = new EventEmitter<Partial<ViewFieldNodeState> & { id: string }>();
  @Output() viewNodeFormulaChange = new EventEmitter<Record<string, string>>();

  protected readonly ready = signal(false);

  private editor: NodeEditor<any> | null = null;
  private area: AreaPlugin<any, any> | null = null;
  private socket = new ClassicPreset.Socket('field');

  private entityNode: ClassicPreset.Node | null = null;
  private entityFieldNodeByName = new Map<string, ClassicPreset.Node>();
  private entityFieldPosByName = new Map<string, { x: number; y: number }>();
  private viewNodeById = new Map<string, ClassicPreset.Node>();
  private literalNodeIds = new Set<string>();
  private comparatorNodeIds = new Set<string>();
  private formulaNodeIds = new Set<string>();

  private literalByNodeId = new Map<string, { literalKind: LiteralKind; value: unknown; pos: { x: number; y: number } }>();
  private comparatorByNodeId = new Map<string, { op: ComparatorOp; pos: { x: number; y: number } }>();
  private formulaByNodeId = new Map<
    string,
    { fn: FormulaFn; digits?: number; joinChar?: string; dateFormat?: string; listaggOptions?: 'list' | 'distinct'; pos: { x: number; y: number } }
  >();

  async ngAfterViewInit(): Promise<void> {
    await this.init();
    await this.rebuild();
    this.ready.set(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.ready()) return;
    if (changes['entityFields'] || changes['viewNodes'] || changes['connections']) {
      void this.rebuild();
    }
  }

  private async init(): Promise<void> {
    const container = this.containerRef.nativeElement;
    const editor = new NodeEditor<any>();
    const area = new AreaPlugin<any, any>(container);

    const render = new AngularPlugin<any, any>({ injector: this.injector });
    render.addPreset(
      Presets.classic.setup({
        customize: {
          control: (context) => {
            const payload = context.payload as any;
            if (payload?.kind === 'viewFieldControl') return ViewFieldControlComponent;
            if (payload?.kind === 'literalControl') return LiteralControlComponent;
            if (payload?.kind === 'comparatorControl') return ComparatorControlComponent;
            if (payload?.kind === 'formulaControl') return FormulaControlComponent;
            if (context.payload instanceof ClassicPreset.InputControl) return ControlComponent;
            return ControlComponent;
          },
        },
      }),
    );

    const connection = new ConnectionPlugin<any, any>();
    connection.addPreset(ConnectionPresets.classic.setup());

    area.use(render);
    area.use(connection);

    // 接続変更を外へ伝搬
    editor.addPipe((ctx) => {
      if (ctx.type === 'connectioncreate' || ctx.type === 'connectionremove') {
        queueMicrotask(() => {
          this.emitConnections();
          this.emitViewFormulas();
        });
      }
      return ctx;
    });

    this.editor = editor;
    this.area = area;

    // パレットからのドロップ（EntityField チップ配置）
    container.addEventListener('dragover', (e) => {
      e.preventDefault();
    });
    container.addEventListener('drop', (e) => {
      e.preventDefault();
      const dt = (e as DragEvent).dataTransfer;
      if (!dt) return;
      const raw = dt.getData('application/x-indexcore');
      if (!raw) return;
      try {
        const payload = JSON.parse(raw) as { kind: string; fieldName?: string };
        if (payload?.kind !== 'entityField' || !payload.fieldName) return;
        const rect = container.getBoundingClientRect();
        const x = (e as DragEvent).clientX - rect.left;
        const y = (e as DragEvent).clientY - rect.top;
        void this.addEntityFieldNode(payload.fieldName, { x, y });
      } catch {
        // ignore
      }
    });
  }

  private emitSelectionFromCtx(ctx: any): void {
    const nodeId = ctx?.data?.id;
    if (!nodeId) return;
    // Viewノードだけを選択対象にする
    for (const [viewId, node] of this.viewNodeById.entries()) {
      if (node.id === nodeId) {
        this.selectViewNode.emit(viewId);
        return;
      }
    }
  }

  private safeGetNodes(): ClassicPreset.Node[] {
    try {
      return (this.editor as any)?.getNodes?.() ?? [];
    } catch {
      return [];
    }
  }

  private safeGetConnections(): any[] {
    try {
      return (this.editor as any)?.getConnections?.() ?? [];
    } catch {
      return [];
    }
  }

  private async rebuild(): Promise<void> {
    if (!this.editor || !this.area) return;

    // 全消しして再構築（まずはMVP優先）
    for (const con of this.safeGetConnections()) {
      await this.editor.removeConnection(con);
    }
    for (const node of this.safeGetNodes()) {
      await this.editor.removeNode(node);
    }
    this.viewNodeById.clear();
    this.entityFieldNodeByName.clear();
    this.literalNodeIds.clear();
    this.comparatorNodeIds.clear();
    this.formulaNodeIds.clear();

    // 互換のため、当面は従来の巨大 EntitySchema ノードは出さない（パレットから配置する）
    this.entityNode = null;

    // 既に配置済みの EntityField ノードを復元
    for (const f of this.entityFields) {
      if (!this.entityFieldPosByName.has(f.fieldName)) continue;
      const pos = this.entityFieldPosByName.get(f.fieldName)!;
      await this.addEntityFieldNode(f.fieldName, pos);
    }

    // Literal / Comparator / Formula ノードを復元
    for (const [id, l] of this.literalByNodeId.entries()) {
      await this.addLiteralNode(l.literalKind, l.value, l.pos, id);
    }
    for (const [id, c] of this.comparatorByNodeId.entries()) {
      await this.addComparatorNode(c.op, c.pos, id);
    }
    for (const [id, f] of this.formulaByNodeId.entries()) {
      await this.addFormulaNode(f.fn, f.pos, id, f);
    }

    // ViewField ノード（複数）
    let y = 40;
    for (const v of this.viewNodes) {
      const node = new ClassicPreset.Node('ViewField');
      node.addInput('in', new ClassicPreset.Input(this.socket, 'in'));
      const control: ViewFieldControlPayload = {
        kind: 'viewFieldControl',
        viewNodeId: v.id,
        fieldName: v.fieldName ?? '',
        fieldType: v.fieldType ?? '',
        overrideFieldName: !!(v as any).overrideFieldName,
        onPatch: (patch) => this.patchViewNode.emit(patch as any),
      };
      node.addControl('viewField', control as any);
      await this.editor.addNode(node as any);
      await this.area.translate(node.id as any, { x: 520, y });
      y += 160;
      this.viewNodeById.set(v.id, node);
    }

    // 接続復元
    for (const c of this.connections) {
      const viewNode = this.viewNodeById.get(c.toViewNodeId);
      if (!viewNode) continue;
      const entityFieldNode = this.entityFieldNodeByName.get(c.fromEntityFieldName);
      if (!entityFieldNode) continue;
      if (!(entityFieldNode.outputs as any)?.value) continue;
      const con = new ClassicPreset.Connection(entityFieldNode, 'value', viewNode, 'in');
      await this.editor.addConnection(con as any);
    }

    this.emitConnections();
    this.emitViewFormulas();
  }

  async addEntityFieldNode(fieldName: string, position?: { x: number; y: number }): Promise<void> {
    if (!this.editor || !this.area) return;
    if (this.entityFieldNodeByName.has(fieldName)) return;

    const node = new ClassicPreset.Node(`EntityField: ${fieldName}`);
    node.addOutput('value', new ClassicPreset.Output(this.socket, 'value'));
    await this.editor.addNode(node as any);
    const x = position?.x ?? 40;
    const y = position?.y ?? 120;
    await this.area.translate(node.id as any, { x, y });
    this.entityFieldNodeByName.set(fieldName, node);
    this.entityFieldPosByName.set(fieldName, { x, y });
    this.emitConnections();
  }

  async addLiteralNode(
    literalKind: LiteralKind,
    value?: unknown,
    position?: { x: number; y: number },
    fixedNodeId?: string,
  ): Promise<void> {
    if (!this.editor || !this.area) return;
    const node = new ClassicPreset.Node('Literal');
    node.addOutput('value', new ClassicPreset.Output(this.socket, 'value'));
    const payload: LiteralControlPayload = {
      kind: 'literalControl',
      literalKind,
      value: value ?? (literalKind === 'number' ? 0 : literalKind === 'string[]' ? [] : ''),
      onChange: (next) => {
        payload.value = next;
        this.literalByNodeId.set(String(node.id), {
          literalKind: payload.literalKind,
          value: payload.value,
          pos: this.literalByNodeId.get(String(node.id))?.pos ?? { x: 260, y: 120 },
        });
        // 再描画のために rebuild（重いがMVP優先）
        void this.rebuild();
      },
    };
    node.addControl('literal', payload as any);
    if (fixedNodeId) (node as any).id = fixedNodeId;
    await this.editor.addNode(node as any);
    const x = position?.x ?? 260;
    const y = position?.y ?? 120;
    await this.area.translate(node.id as any, { x, y });
    this.literalNodeIds.add(String(node.id));
    if (!fixedNodeId) {
      this.literalByNodeId.set(String(node.id), { literalKind, value: payload.value, pos: { x, y } });
    }
  }

  async addComparatorNode(op: ComparatorOp, position?: { x: number; y: number }, fixedNodeId?: string): Promise<void> {
    if (!this.editor || !this.area) return;
    const node = new ClassicPreset.Node('Comparator');
    node.addInput('a', new ClassicPreset.Input(this.socket, 'a'));
    node.addInput('b', new ClassicPreset.Input(this.socket, 'b'));
    node.addOutput('result', new ClassicPreset.Output(this.socket, 'result'));
    const payload: ComparatorControlPayload = {
      kind: 'comparatorControl',
      op,
      onChange: (next) => {
        payload.op = next;
        this.comparatorByNodeId.set(String(node.id), {
          op: payload.op,
          pos: this.comparatorByNodeId.get(String(node.id))?.pos ?? { x: 360, y: 240 },
        });
        void this.rebuild();
      },
    };
    node.addControl('op', payload as any);
    if (fixedNodeId) (node as any).id = fixedNodeId;
    await this.editor.addNode(node as any);
    const x = position?.x ?? 360;
    const y = position?.y ?? 240;
    await this.area.translate(node.id as any, { x, y });
    this.comparatorNodeIds.add(String(node.id));
    if (!fixedNodeId) this.comparatorByNodeId.set(String(node.id), { op, pos: { x, y } });
  }

  async addFormulaNode(
    fn: FormulaFn,
    position?: { x: number; y: number },
    fixedNodeId?: string,
    seed?: Partial<{
      digits?: number;
      joinChar?: string;
      dateFormat?: string;
      listaggOptions?: 'list' | 'distinct';
    }>,
  ): Promise<void> {
    if (!this.editor || !this.area) return;
    const node = new ClassicPreset.Node('Formula');
    node.addInput('a', new ClassicPreset.Input(this.socket, 'a'));
    node.addInput('b', new ClassicPreset.Input(this.socket, 'b'));
    node.addInput('c', new ClassicPreset.Input(this.socket, 'c'));
    node.addInput('d', new ClassicPreset.Input(this.socket, 'd'));
    node.addInput('e', new ClassicPreset.Input(this.socket, 'e'));
    node.addOutput('result', new ClassicPreset.Output(this.socket, 'result'));

    const payload: FormulaControlPayload = {
      kind: 'formulaControl',
      fn,
      digits: seed?.digits,
      joinChar: seed?.joinChar,
      dateFormat: seed?.dateFormat,
      listaggOptions: seed?.listaggOptions,
      onChange: (next) => {
        Object.assign(payload, next);
        this.formulaByNodeId.set(String(node.id), {
          fn: payload.fn,
          digits: payload.digits,
          joinChar: payload.joinChar,
          dateFormat: payload.dateFormat,
          listaggOptions: payload.listaggOptions,
          pos: this.formulaByNodeId.get(String(node.id))?.pos ?? { x: 520, y: 120 },
        });
        void this.rebuild();
      },
    };
    node.addControl('fn', payload as any);
    if (fixedNodeId) (node as any).id = fixedNodeId;
    await this.editor.addNode(node as any);
    const x = position?.x ?? 520;
    const y = position?.y ?? 120;
    await this.area.translate(node.id as any, { x, y });
    this.formulaNodeIds.add(String(node.id));
    if (!fixedNodeId) {
      this.formulaByNodeId.set(String(node.id), {
        fn,
        digits: payload.digits,
        joinChar: payload.joinChar,
        dateFormat: payload.dateFormat,
        listaggOptions: payload.listaggOptions,
        pos: { x, y },
      });
    }
  }

  private emitViewFormulas(): void {
    if (!this.editor) return;
    const cons = this.safeGetConnections() as any[];
    const connections: GraphConnection[] = cons.map((c) => ({
      source: String(c.source),
      sourceOutput: String(c.sourceOutput),
      target: String(c.target),
      targetInput: String(c.targetInput),
    }));

    const nodes: GraphNodeInfo[] = [];
    for (const [fieldName, node] of this.entityFieldNodeByName.entries()) {
      nodes.push({ kind: 'entityField', nodeId: String(node.id), fieldName });
    }
    for (const [nodeId, l] of this.literalByNodeId.entries()) {
      nodes.push({ kind: 'literal', nodeId, literalKind: l.literalKind, value: l.value });
    }
    for (const [nodeId, c] of this.comparatorByNodeId.entries()) {
      nodes.push({ kind: 'comparator', nodeId, op: c.op });
    }
    for (const [nodeId, f] of this.formulaByNodeId.entries()) {
      nodes.push({
        kind: 'formula',
        nodeId,
        fn: f.fn,
        digits: f.digits,
        joinChar: f.joinChar,
        dateFormat: f.dateFormat,
        listaggOptions: f.listaggOptions,
      });
    }

    const emitter = buildFormulaEmitter(nodes, connections);
    const out: Record<string, string> = {};
    for (const [viewId, node] of this.viewNodeById.entries()) {
      const expr = emitter.emitFromInput(String(node.id), 'in');
      if (expr) out[viewId] = expr;
    }
    this.viewNodeFormulaChange.emit(out);
  }

  private emitConnections(): void {
    if (!this.editor) return;
    const out: ReteConnection[] = [];
    const cons = this.safeGetConnections();
    for (const con of cons) {
      const fromNode = [...this.entityFieldNodeByName.entries()].find(([, n]) => n.id === con.source)?.[0];
      if (!fromNode) continue;
      const from = String(fromNode);
      const toNodeId = con.target;
      // targetノードid -> viewNodeId を逆引き
      const toView = [...this.viewNodeById.entries()].find(([, n]) => n.id === toNodeId)?.[0];
      if (!toView) continue;
      out.push({ fromEntityFieldName: from, toViewNodeId: toView });
    }
    this.connectionsChange.emit(out);
  }
}

